import _JSON$stringify from 'babel-runtime/core-js/json/stringify';
import _regeneratorRuntime from 'babel-runtime/regenerator';
import _objectWithoutProperties from 'babel-runtime/helpers/objectWithoutProperties';
import _typeof from 'babel-runtime/helpers/typeof';
import _asyncToGenerator from 'babel-runtime/helpers/asyncToGenerator';

var _this = this;

import multihash from 'multihashes';
import axios from 'axios';
import * as errorConstants from '../constants/error';
import { functions as hashFunctions } from '../constants/hash';
import isRequired from './isRequired';
import { getURISuffix } from './uri';
/**
 * Validate a file. The file must include the hash as the suffix of the URI,
 * or the hash can be passed via the options parameter or using the Evidence standard
 * self hash.
 * @param {string} fileURI - The URI of where the file data can be fetched
 * @param {object} options - The optional paramaters that can be used to validate the file.
 * @returns {object} The file as well as the validity of the hashes
 */

var validateFileFromURI = function () {
  var _ref = _asyncToGenerator(
  /*#__PURE__*/
  _regeneratorRuntime.mark(function _callee() {
    var fileURI = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : isRequired('fileURI');
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

    var fileResponse, fileContent, selfHash, _fileContent2, _selfHash, _fileContent, isValid;

    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.next = 2;
            return axios.get(fileURI);

          case 2:
            fileResponse = _context.sent;

            if (!(fileResponse.status !== 200)) {
              _context.next = 5;
              break;
            }

            throw new Error(errorConstants.HTTP_ERROR('Unable to fetch file at ' + fileURI + '. Returned status code ' + fileResponse.status));

          case 5:
            fileContent = fileResponse.data; // If from ipfs or other source that validates hashes we cam return file

            if (!options.preValidated) {
              _context.next = 8;
              break;
            }

            return _context.abrupt('return', {
              file: fileContent,
              isValid: true
            });

          case 8:
            selfHash = null; // If we are validating evidence check for optional selfHash key

            if ((typeof fileContent === 'undefined' ? 'undefined' : _typeof(fileContent)) === 'object') {
              _fileContent2 = fileContent, _selfHash = _fileContent2.selfHash, _fileContent = _objectWithoutProperties(_fileContent2, ['selfHash']);
              fileContent = _fileContent;
              selfHash = _selfHash;
            }

            isValid = void 0; // If there is an error validating the hash just assume it is invalid so that legacy disputes can be fetched

            try {
              isValid = validMultihash(options.hash || selfHash || getURISuffix(fileURI), fileContent, options.customHashFn);
            } catch (err) {
              console.error(err);
              isValid = false;
            }

            if (!(!isValid && options.strictHashes)) {
              _context.next = 14;
              break;
            }

            throw new Error(errorConstants.VALIDATION_ERROR('Evidence hash validation failed'));

          case 14:
            return _context.abrupt('return', {
              file: fileContent,
              isValid: isValid
            });

          case 15:
          case 'end':
            return _context.stop();
        }
      }
    }, _callee, _this);
  }));

  return function validateFileFromURI() {
    return _ref.apply(this, arguments);
  };
}();
/**
 * Validate a multihash.
 * @param {string} multihashHex - The hexadecimal hash.
 * @param {object|string} file - The object we are validating against.
 * @param {fn} customHashFn - <optional> A custom hash function used for file.
 * @returns {bool} If the hashes match.
 */


export { validateFileFromURI };
export var validMultihash = function validMultihash() {
  var multihashHex = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : isRequired('multihashHex');
  var file = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : isRequired('file');
  var customHashFn = arguments[2];
  if ((typeof file === 'undefined' ? 'undefined' : _typeof(file)) === 'object') file = _JSON$stringify(file); // Decode hash to get hashing algorithm

  var decodedHash = void 0;

  try {
    decodedHash = multihash.decode(multihash.fromB58String(multihashHex));
  } catch (err) {
    console.error(err);
    throw new Error(errorConstants.VALIDATION_ERROR('Unable to decode multihash hex. Is your hash base58?'));
  }

  var hashFn = customHashFn || hashFunctions[decodedHash.code];
  if (!hashFn) throw new Error(errorConstants.VALIDATION_ERROR('No hash function for multicode ' + decodedHash.code)); // Hash the original object

  var fileHash = hashFn(file);
  if (fileHash.indexOf('0x') !== 0) fileHash = '0x' + fileHash; // ensure they both have the same prefix

  var decodedHashHex = decodedHash.digest.toString();
  if (decodedHashHex.indexOf('0x') !== 0) decodedHashHex = '0x' + decodedHashHex;
  return fileHash === decodedHashHex;
};
/**
 * Create a base58 multihash from a file.
 * @param {object|string} file - The object we are hashing.
 * @param {number} multicode - The multicode of the hashing algorithm.
 * @param {fn} customHashFn - <optional> A custom hash function used for file.
 * @returns {string} base58 multihash.
 */

export var multihashFile = function multihashFile() {
  var file = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : isRequired('file');
  var multicode = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : isRequired('multicode');
  var customHashFn = arguments[2];
  if ((typeof file === 'undefined' ? 'undefined' : _typeof(file)) === 'object') file = _JSON$stringify(file);
  var hashFn = customHashFn || hashFunctions[multicode];
  if (!hashFn) throw new Error('Hashing Error: Unsupported multicode ' + multicode);
  var fileHash = hashFn(file);
  var encoded = multihash.encode(Buffer.from(fileHash), multicode);
  return multihash.toB58String(encoded);
};